import * as React from "react";
import { useLocation } from "react-router";

import { Button } from "@/components/ui/button";
import {
  useDismissWelcome,
  useOnboardingProgress,
  type OnboardingPersona,
} from "@/api/onboarding";
import { useAuthStore } from "@/store/authStore";
import { useMe } from "@/api/me";
import { personaHome } from "@/components/layout/nav";
import { useModalKeys } from "@/lib/modalKeys";
import { useProductTour } from "@/lib/tour/useProductTour";
import { useViewport } from "@/lib/useViewport";

/**
 * First sign-in welcome (canvas 01b `auth/Welcome`, MV2-911).
 *
 * Fires ONCE, on the persona's actual landing route — `WelcomeOnLanding`
 * mounts it from AppShell when the location is `personaHome(me)`. Today it
 * mounted only on /getting-started, which DashboardRouter sends nobody to,
 * so the modal was effectively unreachable.
 *
 * Board: 460 wide, 22 padding, eyebrow "First sign-in", 22 px title with the
 * first name, one paragraph, Skip for now · Take the {persona} tour, and the
 * "Esc · ⌘↵" hint. Either CTA POSTs /me/welcome/dismiss; the tour CTA starts
 * the persona's OWN tour here (no window event to a page that may not be
 * mounted). `?welcome=1` force-opens for replay.
 */
const PERSONA_COPY: Record<
  OnboardingPersona,
  { title: string; pitch: (client: string) => string; cta: string }
> = {
  superadmin: {
    title: "You have full platform access",
    pitch: () =>
      "Every client, every site. A 90-second tour covers impersonation, MLOps and the audit log.",
    cta: "Take the platform tour",
  },
  clientadmin: {
    title: "Let's set up your fleet",
    pitch: () =>
      "Add customers, register sites, invite your team — a 90-second tour shows you the rooms.",
    cta: "Take the admin tour",
  },
  om: {
    title: "Your work queue is ready",
    pitch: (client) =>
      `Inbox holds every alarm, ticket and SLA risk across ${client}'s sites in one ordered list. A 90-second tour shows you the four places you'll live in.`,
    cta: "Take the operator tour",
  },
  siteowner: {
    title: "Welcome to your plant",
    pitch: () =>
      "Today's energy, earnings and CO₂ avoided — and the bigger picture you've made. A 90-second tour shows you around.",
    cta: "Take the plant tour",
  },
};

export function WelcomeModal(): React.ReactElement | null {
  const me = useMe();
  const previewPersona = useAuthStore((s) => s.previewPersona);
  const viewingAs = useAuthStore((s) => s.viewingAs != null);
  const progress = useOnboardingProgress({ previewPersona });
  const dismiss = useDismissWelcome();
  const tour = useProductTour();

  const [open, setOpen] = React.useState(false);
  const [forced, setForced] = React.useState(false);

  // ?welcome=1 deeplink — bypasses the dismissed flag for replay.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("welcome") === "1") {
      setForced(true);
      setOpen(true);
    }
  }, []);

  // Auto-open after /me + progress hydrate. Skip when forced — already open.
  // Never while viewing as a user (MV2-1250): it is their welcome, and the
  // dismiss is a write the read-only view refuses, so it would re-fire.
  React.useEffect(() => {
    if (forced || viewingAs) return;
    if (!me.data || !progress.data) return;
    if (!progress.data.welcome_dismissed) setOpen(true);
  }, [me.data, progress.data, forced, viewingAs]);

  const persona = progress.data?.persona;
  const copy = persona ? PERSONA_COPY[persona] : null;
  const firstName = me.data?.full_name?.split(" ")[0] || "";
  const client = me.data?.client?.name ?? "your team";

  const close = React.useCallback(
    (takeTheTour: boolean) => {
      setOpen(false);
      // Errors are silent — the modal re-firing is the worst case.
      dismiss.mutate();
      if (takeTheTour && persona) {
        // One frame so driver.js mounts with the modal gone.
        requestAnimationFrame(() => tour.start(persona, { resume: false }));
      }
    },
    [dismiss, persona, tour],
  );

  // Spec 17 §4.1 keyboard contract — Esc closes, ⌘/Ctrl+↵ confirms.
  useModalKeys({
    enabled: open,
    onEscape: () => close(false),
    onSubmit: () => close(true),
  });

  if (!open || !copy) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--gray-900)]/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-modal-title"
      onMouseDown={(e) => {
        // Backdrop click closes only when the press starts on the backdrop.
        if (e.target === e.currentTarget) close(false);
      }}
    >
      <div className="w-full max-w-[460px] rounded-[14px] border border-gray-200 bg-surface p-[22px] shadow-xl">
        <div className="type-eyebrow text-gray-500">First sign-in</div>
        <h2
          id="welcome-modal-title"
          className="mt-1 text-[22px] font-semibold leading-[1.15] tracking-[-0.015em] text-gray-900"
        >
          {copy.title}
          {firstName ? `, ${firstName}` : ""}
        </h2>
        <p className="mt-2 text-[13px] leading-[1.45] text-gray-700">
          {copy.pitch(client)}
        </p>
        <div className="mt-5 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => close(false)}
            data-testid="welcome-skip"
          >
            Skip for now
          </Button>
          <span className="flex-1" />
          <Button
            variant="primary"
            size="sm"
            type="button"
            onClick={() => close(true)}
            data-testid="welcome-take-tour"
          >
            {copy.cta}
          </Button>
        </div>
        <div className="mt-2.5 text-right text-[11px] text-gray-500">
          Esc · ⌘↵
        </div>
      </div>
    </div>
  );
}

/**
 * Mounts the welcome on the persona's landing route only (or anywhere with
 * `?welcome=1`). Rendered once from AppShell so every shell — console,
 * owner, phone — gets the same first-sign-in moment.
 */
export function WelcomeOnLanding(): React.ReactElement | null {
  const { pathname, search } = useLocation();
  const { data: me } = useMe();
  const { isCompact } = useViewport();
  if (!me) return null;
  const forced = new URLSearchParams(search).get("welcome") === "1";
  const landing = personaHome(me.primary_persona, { isMobile: isCompact });
  if (!forced && pathname !== landing) return null;
  return <WelcomeModal />;
}
